class StickyProductOptions extends HTMLElement {
	connectedCallback() {
		this.abortController = new AbortController();
		const { signal } = this.abortController;
		this.formElement = document.getElementById(this.getAttribute('form'));
		this.addEventListener('change', (event) => this.onChange(event), { signal });
		this.formElement?.addEventListener('product:rerender', () => this.requestController?.abort(), { signal });
		this.formElement?.addEventListener('variant:change', () => this.syncSizeLabels(), { signal });
		document.documentElement.addEventListener('size-unit:change', () => this.syncSizeLabels(), { signal });
		this.syncSizeLabels();
	}

	disconnectedCallback() {
		this.abortController.abort();
		this.requestController?.abort();
	}

	syncSizeLabels() {
		// Reuse the PDP's size labels and conversion table; all other options keep their own names.
		const unit = document.documentElement.dataset.sizeUnit || 'eu';
		Array.from(this.formElement?.elements || []).forEach((input) => {
			if (!input.matches('input[data-option-position]')) return;
			const label = input.labels[0]?.querySelector(`.size-label__unit--${unit}`);
			const option = this.querySelector(`option[value="${input.value}"]`);
			if (label && option) option.textContent = label.textContent + (option.dataset.availability || '');
		});
	}

	async onChange(event) {
		if (!(event.target instanceof HTMLSelectElement) || this.inert) return;
		const selects = Array.from(this.querySelectorAll('select'));
		const productUrl = event.target.selectedOptions[0]?.dataset.productUrl;
		if (productUrl) {
			window.location.assign(productUrl);
			return;
		}
		const picker = document.querySelector(`variant-picker[form-id="${this.getAttribute('form')}"]`);
		if (!picker) return;
		// Unchanged options use the PDP selection, so even the first change refreshes dependent values.
		const selectedInputs = Array.from(this.formElement.elements).filter((input) => input.matches('input[data-option-position]:checked'));
		const optionValues = selects.map((select, index) => select.value || selectedInputs.find((input) => Number(input.dataset.optionPosition) === index + 1)?.value);
		if (optionValues.some((value) => !value)) return;
		const error = this.querySelector('[data-sticky-error]');
		const name = event.target.name;
		error.hidden = true;
		this.inert = true;
		this.setAttribute('aria-busy', 'true');
		const requestController = this.requestController = new AbortController();
		let updated = false;
		try {
			updated = await this.updateVariant(picker, optionValues, requestController.signal);
		} catch {
			if (this.isConnected && picker.isConnected) {
				selects.forEach((select) => { select.value = select.querySelector('option[selected]')?.value || ''; });
				error.hidden = false;
			}
		} finally {
			this.requestController = null;
			this.inert = false;
			this.removeAttribute('aria-busy');
			if (this.isConnected && (updated || picker.isConnected)) {
				this.querySelector(`select[name="${name}"]`)?.focus({ preventScroll: true });
			}
		}
	}

	async updateVariant(picker, optionValues, signal) {
		// Prestige's cached selectCombination promise can stay pending after a failed fetch.
		// Own the cancellable request, then use the theme's normal rendering events.
		const url = new URL(`${window.Shopify.routes.root}products/${picker.productHandle}`, window.location.origin);
		url.searchParams.set('option_values', optionValues.join(','));
		url.searchParams.set('section_id', picker.getAttribute('section-id'));
		const response = await fetch(url, { signal });
		if (!response.ok) throw new Error('Could not load the selected variant');
		const htmlFragment = document.createRange().createContextualFragment(await response.text());
		if (signal.aborted || !this.isConnected || !picker.isConnected) return false;

		const variantData = htmlFragment.querySelector(`variant-picker[form-id="${this.getAttribute('form')}"] script[data-variant]`);
		const stickyBarId = this.querySelector('product-rerender').id;
		if (!variantData || !htmlFragment.getElementById(stickyBarId)) throw new Error('Incomplete variant response');
		const variant = JSON.parse(variantData.textContent);
		const previousVariant = picker.selectedVariant;
		const variantInput = this.formElement.elements.namedItem('id');
		variantInput.value = variant?.id || '';
		variantInput.dispatchEvent(new Event('change', { bubbles: true }));
		if (picker.updateUrl && variant?.id) {
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.set('variant', variant.id);
			window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
		}
		this.formElement.dispatchEvent(new CustomEvent('product:rerender', {
			detail: { htmlFragment, productChange: false },
		}));
		this.formElement.dispatchEvent(new CustomEvent('variant:change', {
			bubbles: true,
			detail: { formId: this.getAttribute('form'), variant, previousVariant },
		}));
		window.Shopify.PaymentButton?.init();
		return true;
	}
}

if (!customElements.get('sticky-product-options')) {
	customElements.define('sticky-product-options', StickyProductOptions);
}
